<template>
    <div class="analytics-dashboard container-fluid py-4">
        <!-- Status Banner — from status.solana.com -->
        <div :class="['status-banner', 'status-banner--' + statusLevel, 'mb-4']">
            <div class="status-banner-inner">
                <div class="status-indicator">
                    <span :class="['status-dot', 'status-dot--' + statusLevel]"></span>
                </div>
                <div class="status-text">
                    <h1 class="status-headline">{{ statusDescription }}</h1>
                    <p class="status-subtitle">
                        Source: <a href="https://status.solana.com" target="_blank" rel="noopener" class="status-source-link">status.solana.com</a>
                        <span class="status-auto-refresh">&middot; Updated {{ lastUpdatedLabel }}</span>
                    </p>
                </div>
                <div class="status-badge-group">
                    <span :class="['status-uptime-badge', activeIncidents > 0 ? 'status-uptime-badge--warn' : '']">
                        {{ activeIncidents > 0 ? activeIncidents + ' active incident' + (activeIncidents > 1 ? 's' : '') : 'No active incidents' }}
                    </span>
                </div>
            </div>
        </div>

        <!-- Hero Stats Row -->
        <div class="analytics-hero mb-4">
            <div class="analytics-hero-stat hero-stat--cyan">
                <div class="hero-label">Total TPS</div>
                <div class="hero-value font-mono-num">{{ formatNumber(blockSummary.totalTps) }}</div>
                <div class="hero-subtitle">incl. votes &amp; failed</div>
            </div>
            <div class="analytics-hero-stat hero-stat--green">
                <div class="hero-label">User TPS</div>
                <div class="hero-value font-mono-num">{{ formatNumber(activitySummary.currentUserTps) }}</div>
                <div class="hero-subtitle">successful non-vote</div>
            </div>
            <div class="analytics-hero-stat">
                <div class="hero-label">Non-Vote TPS</div>
                <div class="hero-value font-mono-num">{{ formatNumber(blockSummary.nonVoteTps) }}</div>
                <div class="hero-subtitle">success + failed</div>
            </div>
            <div class="analytics-hero-stat hero-stat--amber">
                <div class="hero-label">Fail Rate</div>
                <div class="hero-value font-mono-num">{{ blockSummary.failRate || 0 }}%</div>
                <div class="hero-subtitle">of non-vote txns</div>
            </div>
            <div class="analytics-hero-stat hero-stat--blue">
                <div class="hero-label">Avg Slot Time</div>
                <div class="hero-value font-mono-num">{{ blockSummary.avgSlotTime || 0 }}ms</div>
                <div class="hero-subtitle">target: 400ms</div>
            </div>
            <div class="analytics-hero-stat">
                <div class="hero-label">CU Utilization</div>
                <div class="hero-value font-mono-num">{{ activitySummary.computeUtilization || 0 }}%</div>
                <div class="hero-subtitle">of requested limit</div>
            </div>
        </div>

        <!-- Real-time TPS Chart (block-metrics: full picture) -->
        <div class="section-divider">
            <span class="section-title">Real-time Network Activity</span>
        </div>
        <div class="analytics-row cols-1 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Total TPS Breakdown</div>
                    <div class="panel-subtitle">Votes, successful, and failed transactions per minute (block-metrics)</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="tpsBreakdownChart"></canvas>
                </div>
            </div>
        </div>

        <!-- User TPS + Fee Revenue -->
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">User Transaction Rate</div>
                    <div class="panel-subtitle">Successful non-vote transactions per second</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="userTpsChart"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Fee Revenue (SOL)</div>
                    <div class="panel-subtitle">Priority fees and Jito tips per minute</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="feeChart"></canvas>
                </div>
            </div>
        </div>

        <!-- 24h Hourly Overview -->
        <div class="section-divider">
            <span class="section-title">24-Hour Overview</span>
        </div>
        <div class="analytics-row cols-2 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Hourly User TPS (24h)</div>
                    <div class="panel-subtitle">Average successful non-vote TPS per hour</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="hourlyTpsChart"></canvas>
                </div>
            </div>
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Hourly Fees (24h)</div>
                    <div class="panel-subtitle">Priority fees and Jito tips per hour (SOL)</div>
                </div>
                <div class="panel-body chart-tall">
                    <canvas ref="hourlyFeeChart"></canvas>
                </div>
            </div>
        </div>

        <!-- Uptime Streak Banner -->
        <div class="section-divider">
            <span class="section-title">Network Reliability</span>
        </div>
        <div v-if="uptimeStats.daysSinceLastSevere !== null" class="uptime-streak-banner mb-4">
            <div class="uptime-streak-inner">
                <div class="uptime-streak-icon">&#9889;</div>
                <div class="uptime-streak-content">
                    <div class="uptime-streak-headline">
                        <span class="uptime-streak-days font-mono-num">{{ formatUptime(uptimeStats.daysSinceLastSevere) }}</span>
                        since last major outage
                    </div>
                    <div class="uptime-streak-detail">
                        Last critical incident: <strong>{{ uptimeStats.lastSevereIncident?.name }}</strong>
                        &mdash; {{ formatIncidentDate(uptimeStats.lastSevereIncident?.startedAt) }}
                        <span class="uptime-streak-ago">({{ timeAgo(uptimeStats.lastSevereIncident?.startedAt) }})</span>
                    </div>
                </div>
                <div class="uptime-streak-stats">
                    <div class="uptime-mini-stat" v-for="(count, year) in uptimeStats.byYear" :key="year">
                        <span class="uptime-mini-year">{{ year }}</span>
                        <span class="uptime-mini-count font-mono-num">{{ count }}</span>
                        <span class="uptime-mini-label">{{ count === 1 ? 'incident' : 'incidents' }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Incident History from status.solana.com -->
        <div class="analytics-row cols-1 mb-4">
            <div class="analytics-panel">
                <div class="panel-header">
                    <div class="panel-title">Incident History</div>
                    <div class="panel-subtitle">
                        All {{ incidents.length }} recorded incidents from
                        <a href="https://status.solana.com" target="_blank" rel="noopener">status.solana.com</a>
                        &mdash; grouped by year
                    </div>
                </div>
                <div class="panel-body">
                    <div v-if="incidents.length === 0" class="no-incidents">
                        <span class="no-incidents-icon">&#10003;</span>
                        <span>No incidents recorded</span>
                    </div>
                    <div v-else class="incident-list">
                        <template v-for="(group, year) in incidentsByYear" :key="year">
                            <div class="incident-year-header">
                                <span class="incident-year-label">{{ year }}</span>
                                <span class="incident-year-count">{{ group.length }} {{ group.length === 1 ? 'incident' : 'incidents' }}</span>
                                <span class="incident-year-separator"></span>
                            </div>
                            <div v-for="incident in (expandedYears[year] ? group : group.slice(0, 3))" :key="incident.id"
                                 :class="['incident-item', 'incident-item--' + incident.impact, { 'incident-item--ongoing': incident.status !== 'resolved', 'incident-item--old': isOld(incident.startedAt) }]">
                                <div class="incident-indicator">
                                    <span :class="['incident-dot', 'incident-dot--' + incident.impact, incident.status !== 'resolved' ? 'incident-dot--active' : '']"></span>
                                </div>
                                <div class="incident-details">
                                    <div class="incident-header">
                                        <span class="incident-title">{{ incident.name }}</span>
                                        <div class="incident-badges">
                                            <span :class="['incident-impact-badge', 'incident-impact-badge--' + incident.impact]">
                                                {{ incident.impact }}
                                            </span>
                                            <span class="incident-ago-badge">{{ timeAgo(incident.startedAt) }}</span>
                                        </div>
                                    </div>
                                    <div class="incident-meta">
                                        <span class="incident-time">{{ formatIncidentDate(incident.startedAt) }}</span>
                                        <span v-if="incident.resolvedAt" class="incident-duration">
                                            &middot; Resolved in {{ formatDuration(incident.startedAt, incident.resolvedAt) }}
                                        </span>
                                        <span v-else class="incident-status-ongoing">&middot; Ongoing</span>
                                    </div>
                                    <div v-if="expandedIncident === incident.id" class="incident-updates">
                                        <div v-for="(update, i) in incident.updates" :key="i" class="incident-update">
                                            <span :class="['update-status-badge', 'update-status-badge--' + update.status]">{{ update.status }}</span>
                                            <span class="update-body">{{ update.body }}</span>
                                            <span class="update-time">{{ formatIncidentDate(update.createdAt) }}</span>
                                        </div>
                                    </div>
                                    <button class="incident-toggle" @click="toggleIncident(incident.id)">
                                        {{ expandedIncident === incident.id ? 'Hide details' : 'Show details' }}
                                    </button>
                                </div>
                                <div class="incident-link" v-if="incident.shortlink">
                                    <a :href="incident.shortlink" target="_blank" rel="noopener" title="View on status.solana.com">&#8599;</a>
                                </div>
                            </div>
                            <button v-if="group.length > 3 && !expandedYears[year]" class="show-more-btn" @click="expandedYears[year] = true">
                                Show all {{ group.length }} incidents from {{ year }}
                            </button>
                        </template>
                    </div>
                </div>
            </div>
        </div>

        <!-- SEO Content: Solana Uptime & Reliability -->
        <div class="section-divider">
            <span class="section-title">Solana Uptime &amp; Reliability</span>
        </div>
        <div class="analytics-row cols-1 mb-4">
            <div class="analytics-panel seo-content-panel">
                <div class="panel-body seo-content">
                    <h2>Is Solana reliable? Understanding Solana's network uptime</h2>

                    <p>Solana's reputation for network outages is based on events that primarily occurred in 2022, a period during which the network experienced {{ uptimeStats.byYear?.['2022'] || 27 }} incidents — many of them during a concentrated period of stress testing in the network's early mainnet-beta phase. Since then, Solana's reliability has improved dramatically.</p>

                    <div class="seo-highlight-box" v-if="uptimeStats.daysSinceLastSevere">
                        <div class="seo-highlight-stat">
                            <span class="seo-highlight-number font-mono-num">{{ formatUptime(uptimeStats.daysSinceLastSevere) }}</span>
                            <span class="seo-highlight-label">since last critical incident</span>
                        </div>
                        <div class="seo-highlight-stat">
                            <span class="seo-highlight-number font-mono-num">{{ uptimeStats.byYear?.['2025'] || 0 }}</span>
                            <span class="seo-highlight-label">incidents in 2025</span>
                        </div>
                        <div class="seo-highlight-stat">
                            <span class="seo-highlight-number font-mono-num">{{ uptimeStats.byYear?.['2024'] || 0 }}</span>
                            <span class="seo-highlight-label">incidents in 2024</span>
                        </div>
                    </div>

                    <h3>When did Solana last go down?</h3>
                    <p v-if="uptimeStats.lastSevereIncident">
                        The most recent critical incident was "<strong>{{ uptimeStats.lastSevereIncident.name }}</strong>"
                        on {{ formatIncidentDate(uptimeStats.lastSevereIncident.startedAt) }}
                        — <strong>{{ timeAgo(uptimeStats.lastSevereIncident.startedAt) }}</strong>.
                        <span v-if="uptimeStats.lastSevereIncident.resolvedAt">
                            It was resolved in {{ formatDuration(uptimeStats.lastSevereIncident.startedAt, uptimeStats.lastSevereIncident.resolvedAt) }}.
                        </span>
                        Since then, the network has operated continuously without a major outage.
                    </p>

                    <h3>Does Solana go down all the time?</h3>
                    <p>No. This is a common misconception based on events from 2022. During that year, Solana mainnet-beta experienced {{ uptimeStats.byYear?.['2022'] || 27 }} reported incidents as the network operated in beta and encountered challenges with congestion and compute demands. In response, Solana core engineers introduced significant improvements:</p>
                    <ul>
                        <li><strong>QUIC networking protocol</strong> — replaced UDP for more reliable transaction ingestion</li>
                        <li><strong>Stake-weighted Quality of Service (QoS)</strong> — prioritises legitimate validators for transaction forwarding</li>
                        <li><strong>Local fee markets</strong> — isolates congestion to specific programs rather than affecting the whole network</li>
                        <li><strong>Improved scheduler</strong> — better transaction ordering and deduplication</li>
                    </ul>
                    <p>These changes have been effective: {{ currentYearLabel }} has seen only {{ uptimeStats.byYear?.[currentYear] || 0 }} incident{{ (uptimeStats.byYear?.[currentYear] || 0) === 1 ? '' : 's' }} recorded on status.solana.com.</p>

                    <h3>Solana incident history by year</h3>
                    <div class="seo-year-timeline">
                        <div v-for="(count, year) in sortedYears" :key="year" class="seo-year-bar">
                            <span class="seo-year-bar-label">{{ year }}</span>
                            <div class="seo-year-bar-track">
                                <div class="seo-year-bar-fill" :style="{ width: Math.max(2, (count / maxYearCount) * 100) + '%' }"></div>
                            </div>
                            <span class="seo-year-bar-count font-mono-num">{{ count }}</span>
                        </div>
                    </div>

                    <h3>How to verify Solana's current status</h3>
                    <p>
                        This page displays real-time network metrics sourced from Solana Compass's on-chain analytics, combined with the
                        official incident history from <a href="https://status.solana.com" target="_blank" rel="noopener">status.solana.com</a>.
                        The TPS charts above show actual network throughput including votes, successful transactions, and failed transactions.
                        All data updates every 30 seconds.
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Chart from 'chart.js/auto';
import { colors, getLineChartOptions, getStackedBarChartOptions } from '../composables/useChartConfig';
import { formatCompact } from '../composables/useFormatters';

export default {
    name: 'NetworkStatus',

    data() {
        return {
            activitySummary: {},
            activitySeries: [],
            blockSummary: {},
            blockSeries: [],
            hourlyData: [],
            incidents: [],
            uptimeStats: {},
            statusLevel: 'operational',
            statusDescription: 'Checking...',
            lastUpdated: null,
            refreshTimer: null,
            expandedIncident: null,
            expandedYears: {},
        };
    },

    computed: {
        lastUpdatedLabel() {
            if (!this.lastUpdated) return '';
            return this.lastUpdated.toLocaleTimeString();
        },
        activeIncidents() {
            return this.incidents.filter(i => i.status !== 'resolved').length;
        },
        incidentsByYear() {
            const groups = {};
            for (const inc of this.incidents) {
                const year = inc.startedAt ? inc.startedAt.substring(0, 4) : 'Unknown';
                if (!groups[year]) groups[year] = [];
                groups[year].push(inc);
            }
            // Sort years descending
            const sorted = {};
            Object.keys(groups).sort((a, b) => b.localeCompare(a)).forEach(y => { sorted[y] = groups[y]; });
            return sorted;
        },
        currentYear() {
            return new Date().getFullYear().toString();
        },
        currentYearLabel() {
            return this.currentYear;
        },
        sortedYears() {
            if (!this.uptimeStats.byYear) return {};
            const sorted = {};
            Object.keys(this.uptimeStats.byYear).sort().forEach(y => { sorted[y] = this.uptimeStats.byYear[y]; });
            return sorted;
        },
        maxYearCount() {
            if (!this.uptimeStats.byYear) return 1;
            return Math.max(1, ...Object.values(this.uptimeStats.byYear));
        },
    },

    created() {
        this.charts = {};
    },

    mounted() {
        this.initCharts();
        this.loadData();
        this.refreshTimer = setInterval(() => this.loadData(), 30000);
    },

    beforeUnmount() {
        Object.values(this.charts).forEach(c => c?.destroy());
        if (this.refreshTimer) clearInterval(this.refreshTimer);
    },

    methods: {
        initCharts() {
            const lineOpts = getLineChartOptions({ enableZoom: false });

            // TPS breakdown — stacked area (votes, successful, failed)
            this.charts.tpsBreakdown = new Chart(this.$refs.tpsBreakdownChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: {
                    ...lineOpts,
                    scales: {
                        ...lineOpts.scales,
                        y: { ...lineOpts.scales.y, stacked: true },
                    },
                },
            });

            // User TPS line chart
            this.charts.userTps = new Chart(this.$refs.userTpsChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: {
                    ...lineOpts,
                    plugins: { ...lineOpts.plugins, legend: { display: false } },
                },
            });

            // Fee chart
            this.charts.fee = new Chart(this.$refs.feeChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getStackedBarChartOptions({
                    formatCallback: v => v.toFixed(2) + ' SOL',
                }),
            });

            // Hourly TPS
            this.charts.hourlyTps = new Chart(this.$refs.hourlyTpsChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: {
                    ...lineOpts,
                    plugins: { ...lineOpts.plugins, legend: { display: false } },
                },
            });

            // Hourly fees
            this.charts.hourlyFee = new Chart(this.$refs.hourlyFeeChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getStackedBarChartOptions({
                    formatCallback: v => v.toFixed(2) + ' SOL',
                }),
            });
        },

        async loadData() {
            try {
                const response = await fetch('/api/status/health?minutes=60');
                const data = await response.json();

                this.activitySummary = data.activity?.summary || {};
                this.activitySeries = data.activity?.series || [];
                this.blockSummary = data.blockMetrics?.summary || {};
                this.blockSeries = data.blockMetrics?.series || [];
                this.hourlyData = data.hourly || [];
                this.incidents = data.incidents || [];
                this.uptimeStats = data.uptimeStats || {};
                this.statusLevel = data.solanaStatus?.level || 'unknown';
                this.statusDescription = data.solanaStatus?.description || 'Unknown';
                this.lastUpdated = new Date();

                this.updateCharts();
            } catch (e) {
                console.error('Error loading status data:', e);
                this.statusLevel = 'unknown';
                this.statusDescription = 'Unable to fetch status';
            }
        },

        updateCharts() {
            // TPS breakdown from block-metrics (full picture)
            const bm = this.blockSeries;
            const bmLabels = bm.map(d => d.label);

            this.charts.tpsBreakdown.data = {
                labels: bmLabels,
                datasets: [
                    {
                        label: 'Votes',
                        data: bm.map(d => d.voteTps),
                        borderColor: '#6366f1',
                        backgroundColor: 'rgba(99, 102, 241, 0.15)',
                        fill: true,
                        tension: 0.3,
                        pointRadius: 1,
                        order: 3,
                    },
                    {
                        label: 'Successful',
                        data: bm.map(d => d.successTps),
                        borderColor: colors.positive,
                        backgroundColor: 'rgba(34, 197, 94, 0.2)',
                        fill: true,
                        tension: 0.3,
                        pointRadius: 1,
                        order: 2,
                    },
                    {
                        label: 'Failed',
                        data: bm.map(d => d.failedTps),
                        borderColor: colors.negative,
                        backgroundColor: 'rgba(239, 68, 68, 0.15)',
                        fill: true,
                        tension: 0.3,
                        pointRadius: 1,
                        order: 1,
                    },
                ],
            };
            this.charts.tpsBreakdown.update('none');

            // User TPS from tx-summary
            const ts = this.activitySeries;
            const tsLabels = ts.map(d => d.label);

            this.charts.userTps.data = {
                labels: tsLabels,
                datasets: [{
                    label: 'User TPS',
                    data: ts.map(d => d.userTps),
                    borderColor: colors.accent,
                    backgroundColor: 'rgba(16, 247, 207, 0.08)',
                    fill: true,
                    tension: 0.3,
                    pointRadius: 1,
                    pointHoverRadius: 5,
                }],
            };
            this.charts.userTps.update('none');

            // Fee chart
            this.charts.fee.data = {
                labels: tsLabels,
                datasets: [
                    {
                        label: 'Priority Fees',
                        data: ts.map(d => d.priorityFees),
                        backgroundColor: 'rgba(139, 92, 246, 0.7)',
                    },
                    {
                        label: 'Jito Tips',
                        data: ts.map(d => d.jitoTips),
                        backgroundColor: 'rgba(249, 115, 22, 0.7)',
                    },
                ],
            };
            this.charts.fee.update('none');

            // Hourly charts
            const hourly = this.hourlyData;
            const hourlyLabels = hourly.map(d => d.label);

            this.charts.hourlyTps.data = {
                labels: hourlyLabels,
                datasets: [{
                    label: 'User TPS',
                    data: hourly.map(d => d.userTps),
                    borderColor: colors.primary,
                    backgroundColor: 'rgba(139, 92, 246, 0.08)',
                    fill: true,
                    tension: 0.3,
                }],
            };
            this.charts.hourlyTps.update('none');

            this.charts.hourlyFee.data = {
                labels: hourlyLabels,
                datasets: [
                    {
                        label: 'Priority Fees',
                        data: hourly.map(d => d.priorityFees),
                        backgroundColor: 'rgba(139, 92, 246, 0.7)',
                    },
                    {
                        label: 'Jito Tips',
                        data: hourly.map(d => d.jitoTips),
                        backgroundColor: 'rgba(249, 115, 22, 0.7)',
                    },
                ],
            };
            this.charts.hourlyFee.update('none');
        },

        toggleIncident(id) {
            this.expandedIncident = this.expandedIncident === id ? null : id;
        },

        formatIncidentDate(dateStr) {
            if (!dateStr) return '';
            const d = new Date(dateStr);
            return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        },

        formatDuration(start, end) {
            if (!start || !end) return '';
            const ms = new Date(end) - new Date(start);
            const hours = Math.floor(ms / 3600000);
            const minutes = Math.floor((ms % 3600000) / 60000);
            if (hours > 24) {
                const days = Math.floor(hours / 24);
                return `${days}d ${hours % 24}h`;
            }
            if (hours > 0) return `${hours}h ${minutes}m`;
            return `${minutes}m`;
        },

        timeAgo(dateStr) {
            if (!dateStr) return '';
            const ms = Date.now() - new Date(dateStr).getTime();
            const days = Math.floor(ms / 86400000);
            if (days < 1) return 'today';
            if (days === 1) return 'yesterday';
            if (days < 30) return `${days} days ago`;
            const months = Math.floor(days / 30.44);
            if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;
            const years = Math.floor(months / 12);
            const remMonths = months % 12;
            if (remMonths === 0) return `${years} year${years > 1 ? 's' : ''} ago`;
            return `${years}y ${remMonths}m ago`;
        },

        formatUptime(days) {
            if (days === null || days === undefined) return '';
            const years = Math.floor(days / 365);
            const months = Math.floor((days % 365) / 30);
            const remDays = days % 30;
            const parts = [];
            if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`);
            if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
            if (remDays > 0 || parts.length === 0) parts.push(`${remDays} day${remDays !== 1 ? 's' : ''}`);
            return parts.join(', ');
        },

        isOld(dateStr) {
            if (!dateStr) return false;
            return (Date.now() - new Date(dateStr).getTime()) > 365 * 86400000;
        },

        formatCompact(v) { return formatCompact(v); },

        formatNumber(v) {
            if (v === undefined || v === null) return '0';
            return Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
        },
    },
};
</script>

<style scoped>
/* Status Banner */
.status-banner {
    border-radius: var(--compass-radius);
    padding: 1.5rem 2rem;
    border: 1px solid;
    transition: all 0.3s ease;
}

.status-banner--operational {
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.08), rgba(16, 185, 129, 0.04));
    border-color: rgba(34, 197, 94, 0.25);
}

.status-banner--degraded {
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.08), rgba(217, 119, 6, 0.04));
    border-color: rgba(245, 158, 11, 0.25);
}

.status-banner--outage {
    background: linear-gradient(135deg, rgba(239, 68, 68, 0.08), rgba(220, 38, 38, 0.04));
    border-color: rgba(239, 68, 68, 0.25);
}

.status-banner--unknown {
    background: linear-gradient(135deg, rgba(107, 114, 128, 0.08), rgba(75, 85, 99, 0.04));
    border-color: rgba(107, 114, 128, 0.25);
}

.status-banner-inner {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    flex-wrap: wrap;
}

.status-indicator { flex-shrink: 0; }

.status-dot {
    display: inline-block;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    animation: pulse 2s ease-in-out infinite;
}

.status-dot--operational {
    background: var(--compass-success);
    box-shadow: 0 0 12px rgba(34, 197, 94, 0.4);
}

.status-dot--degraded {
    background: #f59e0b;
    box-shadow: 0 0 12px rgba(245, 158, 11, 0.4);
}

.status-dot--outage {
    background: var(--compass-danger);
    box-shadow: 0 0 12px rgba(239, 68, 68, 0.4);
}

.status-dot--unknown {
    background: #6b7280;
    box-shadow: 0 0 12px rgba(107, 114, 128, 0.4);
}

@keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(1.1); }
}

.status-text { flex: 1; min-width: 200px; }

.status-headline {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 0.25rem 0;
    color: #e5e7eb;
}

.status-subtitle { font-size: 0.85rem; color: #9ca3af; margin: 0; }
.status-auto-refresh { opacity: 0.6; }

.status-source-link {
    color: inherit;
    text-decoration: underline;
    text-underline-offset: 2px;
    opacity: 0.8;
}
.status-source-link:hover { opacity: 1; }

.status-badge-group { flex-shrink: 0; }

.status-uptime-badge {
    display: inline-block;
    padding: 0.35rem 0.85rem;
    border-radius: var(--compass-radius-sm);
    font-size: 0.8rem;
    font-weight: 500;
    background: rgba(34, 197, 94, 0.12);
    color: var(--compass-success);
    border: 1px solid rgba(34, 197, 94, 0.2);
}

.status-uptime-badge--warn {
    background: rgba(245, 158, 11, 0.12);
    color: #f59e0b;
    border-color: rgba(245, 158, 11, 0.2);
}

/* Hero stat variants */
.hero-stat--red {
    background: linear-gradient(135deg, rgba(239, 68, 68, 0.08), rgba(220, 38, 38, 0.04));
    border-color: rgba(239, 68, 68, 0.15);
}
.hero-stat--red .hero-value {
    background: linear-gradient(135deg, #ef4444, #dc2626);
    -webkit-background-clip: text;
    background-clip: text;
}

.hero-stat--cyan {
    background: linear-gradient(135deg, rgba(16, 247, 207, 0.08), rgba(20, 184, 166, 0.04));
    border-color: rgba(16, 247, 207, 0.15);
}
.hero-stat--cyan .hero-value {
    background: linear-gradient(135deg, #10f7cf, #14b8a6);
    -webkit-background-clip: text;
    background-clip: text;
}

/* Incident styles */
.no-incidents {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1.5rem;
    color: var(--compass-success);
    font-size: 0.95rem;
}

.no-incidents-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: rgba(34, 197, 94, 0.12);
    color: var(--compass-success);
    font-weight: 700;
    font-size: 0.9rem;
}

.incident-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.5rem;
}

.incident-item {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem 1.25rem;
    border-radius: var(--compass-radius-sm);
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid rgba(255, 255, 255, 0.06);
    transition: background 0.2s;
}

.incident-item:hover { background: rgba(255, 255, 255, 0.04); }

.incident-item--ongoing {
    background: rgba(245, 158, 11, 0.06);
    border-color: rgba(245, 158, 11, 0.2);
}

.incident-indicator { padding-top: 0.3rem; flex-shrink: 0; }

.incident-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #6b7280;
}

.incident-dot--critical { background: var(--compass-danger); }
.incident-dot--major { background: #f97316; }
.incident-dot--minor { background: #f59e0b; }
.incident-dot--maintenance { background: #3b82f6; }
.incident-dot--none { background: #6b7280; }

.incident-dot--active { animation: pulse 1.5s ease-in-out infinite; }

.incident-details { flex: 1; min-width: 0; }

.incident-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.35rem;
}

.incident-title {
    font-weight: 600;
    font-size: 0.95rem;
    color: #e5e7eb;
}

.incident-impact-badge {
    display: inline-block;
    padding: 0.15rem 0.5rem;
    border-radius: var(--compass-radius-sm);
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    flex-shrink: 0;
}

.incident-impact-badge--critical { background: rgba(239, 68, 68, 0.15); color: #ef4444; }
.incident-impact-badge--major { background: rgba(249, 115, 22, 0.15); color: #f97316; }
.incident-impact-badge--minor { background: rgba(245, 158, 11, 0.15); color: #f59e0b; }
.incident-impact-badge--maintenance { background: rgba(59, 130, 246, 0.15); color: #3b82f6; }
.incident-impact-badge--none { background: rgba(107, 114, 128, 0.15); color: #9ca3af; }

.incident-meta {
    font-size: 0.8rem;
    color: #9ca3af;
    margin-bottom: 0.5rem;
}

.incident-status-ongoing {
    color: #f59e0b;
    font-weight: 500;
}

.incident-toggle {
    background: none;
    border: none;
    color: #8b5cf6;
    font-size: 0.8rem;
    cursor: pointer;
    padding: 0;
    text-decoration: underline;
    text-underline-offset: 2px;
}
.incident-toggle:hover { color: #a78bfa; }

.incident-updates {
    margin-top: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    border-left: 2px solid rgba(139, 92, 246, 0.2);
    padding-left: 1rem;
}

.incident-update {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    font-size: 0.85rem;
}

.update-status-badge {
    display: inline-block;
    width: fit-content;
    padding: 0.1rem 0.4rem;
    border-radius: 3px;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
}

.update-status-badge--resolved { background: rgba(34, 197, 94, 0.15); color: #22c55e; }
.update-status-badge--monitoring { background: rgba(59, 130, 246, 0.15); color: #3b82f6; }
.update-status-badge--investigating { background: rgba(245, 158, 11, 0.15); color: #f59e0b; }
.update-status-badge--identified { background: rgba(249, 115, 22, 0.15); color: #f97316; }
.update-status-badge--update { background: rgba(107, 114, 128, 0.15); color: #9ca3af; }

.update-body { color: #d1d5db; line-height: 1.5; }
.update-time { color: #6b7280; font-size: 0.75rem; }

.incident-link { flex-shrink: 0; }
.incident-link a {
    color: #6b7280;
    font-size: 1.1rem;
    text-decoration: none;
}
.incident-link a:hover { color: #8b5cf6; }

/* Uptime Streak Banner */
.uptime-streak-banner {
    border-radius: var(--compass-radius);
    padding: 1.5rem 2rem;
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.06), rgba(16, 185, 129, 0.03));
    border: 1px solid rgba(34, 197, 94, 0.2);
}

.uptime-streak-inner {
    display: flex;
    align-items: flex-start;
    gap: 1.25rem;
    flex-wrap: wrap;
}

.uptime-streak-icon {
    font-size: 2rem;
    line-height: 1;
    flex-shrink: 0;
}

.uptime-streak-content { flex: 1; min-width: 250px; }

.uptime-streak-headline {
    font-size: 1.25rem;
    font-weight: 600;
    color: #e5e7eb;
    margin-bottom: 0.4rem;
}

.uptime-streak-days {
    color: var(--compass-success);
    font-weight: 700;
}

.uptime-streak-detail {
    font-size: 0.85rem;
    color: #9ca3af;
    line-height: 1.5;
}

.uptime-streak-ago {
    color: #6b7280;
    font-style: italic;
}

.uptime-streak-stats {
    display: flex;
    gap: 1.25rem;
    flex-shrink: 0;
}

.uptime-mini-stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.15rem;
    padding: 0.5rem 0.75rem;
    border-radius: var(--compass-radius-sm);
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.06);
    min-width: 60px;
}

.uptime-mini-year { font-size: 0.7rem; color: #6b7280; font-weight: 600; text-transform: uppercase; }
.uptime-mini-count { font-size: 1.25rem; font-weight: 700; color: #e5e7eb; }
.uptime-mini-label { font-size: 0.65rem; color: #6b7280; }

/* Incident year headers */
.incident-year-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 0 0.25rem;
    margin-top: 0.5rem;
}

.incident-year-header:first-child { margin-top: 0; }

.incident-year-label {
    font-size: 1.1rem;
    font-weight: 700;
    color: #e5e7eb;
    flex-shrink: 0;
}

.incident-year-count {
    font-size: 0.8rem;
    color: #6b7280;
    flex-shrink: 0;
}

.incident-year-separator {
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.06);
}

/* Faded old incidents */
.incident-item--old {
    opacity: 0.6;
}
.incident-item--old:hover {
    opacity: 0.85;
}

.incident-badges {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
}

.incident-ago-badge {
    font-size: 0.7rem;
    color: #6b7280;
    font-style: italic;
    white-space: nowrap;
}

/* SEO Content */
.seo-content-panel { border-top: 3px solid rgba(139, 92, 246, 0.3); }

.seo-content {
    padding: 2rem;
    color: #d1d5db;
    line-height: 1.7;
    font-size: 0.95rem;
}

.seo-content h2 {
    color: #e5e7eb;
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 1rem 0;
}

.seo-content h3 {
    color: #e5e7eb;
    font-size: 1.15rem;
    font-weight: 600;
    margin: 1.75rem 0 0.75rem 0;
}

.seo-content p { margin: 0 0 1rem 0; }

.seo-content ul {
    margin: 0 0 1rem 0;
    padding-left: 1.5rem;
}

.seo-content li {
    margin-bottom: 0.5rem;
}

.seo-content a {
    color: #8b5cf6;
    text-decoration: underline;
    text-underline-offset: 2px;
}
.seo-content a:hover { color: #a78bfa; }

.seo-highlight-box {
    display: flex;
    gap: 2rem;
    padding: 1.25rem 1.5rem;
    margin: 1.25rem 0;
    border-radius: var(--compass-radius);
    background: rgba(34, 197, 94, 0.05);
    border: 1px solid rgba(34, 197, 94, 0.15);
    flex-wrap: wrap;
}

.seo-highlight-stat {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.seo-highlight-number {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--compass-success);
}

.seo-highlight-label {
    font-size: 0.8rem;
    color: #9ca3af;
}

.seo-year-timeline {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin: 1rem 0;
}

.seo-year-bar {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.seo-year-bar-label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #e5e7eb;
    width: 40px;
    text-align: right;
}

.seo-year-bar-track {
    flex: 1;
    height: 20px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.04);
    overflow: hidden;
}

.seo-year-bar-fill {
    height: 100%;
    border-radius: 4px;
    background: linear-gradient(90deg, rgba(139, 92, 246, 0.6), rgba(139, 92, 246, 0.3));
    transition: width 0.5s ease;
}

.seo-year-bar-count {
    font-size: 0.85rem;
    font-weight: 600;
    color: #9ca3af;
    width: 24px;
}

.show-more-btn {
    background: none;
    border: 1px solid rgba(139, 92, 246, 0.2);
    color: #8b5cf6;
    padding: 0.5rem 1rem;
    border-radius: var(--compass-radius-sm);
    cursor: pointer;
    font-size: 0.85rem;
    margin-top: 0.5rem;
    transition: all 0.2s;
}
.show-more-btn:hover {
    background: rgba(139, 92, 246, 0.08);
    border-color: rgba(139, 92, 246, 0.4);
}
</style>
